/**
 * Kickoff, Media plans and the Oneflow agreement live in the back office only (2026-09-28).
 * Old links to them (/agents/kickoff, /agents/media-plans, canvas cards, bookmarks) land here and
 * go on to the same path in the BO, query included.
 */
import { isProduction } from '@/utils/env';
import { getStaffScopeTeam, isClientTeamId } from '@/cowork/staffScope';

export function backOfficeOrigin(): string {
  const set = (import.meta.env as Record<string, string | undefined>).VITE_BO_APP_URL;
  if (set) return set.replace(/\/$/, '');
  return isProduction() ? 'https://bo2.beatly.com' : 'https://bo2.staging.beatly.com';
}

export function backOfficeUrl(path: string): string {
  return `${backOfficeOrigin()}${path.startsWith('/') ? path : `/${path}`}`;
}

/** Paths that belong to the back office now. */
export function isBackOfficePath(path: string): boolean {
  return /^\/agents\/(kickoff|media-plans)(?=$|[/?#])/.test(path);
}

/** The BO URL for a path, naming the client this staff session works in (`?team=`) so the BO opens the same one. */
export function backOfficeUrlForClient(path: string): string {
  const team = getStaffScopeTeam();
  if (!isClientTeamId(team)) return backOfficeUrl(path);
  const [base, hash = ''] = path.split('#');
  return backOfficeUrl(`${base}${base.includes('?') ? '&' : '?'}team=${encodeURIComponent(team)}${hash ? `#${hash}` : ''}`);
}

/** Open a BO path: the whole window when Omni is framed inside the BO, else this tab. */
export function openInBackOffice(path: string): void {
  const url = backOfficeUrlForClient(path);
  try {
    if (window.top && window.top !== window) { window.top.location.href = url; return; }
  } catch { /* cross-origin top: fall through */ }
  window.location.assign(url);
}
